/**
 * InvoicePaymentModal - Modal for recording payments
 * Uses mobile-safe dialog pattern with visualViewport-based max-height
 */

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CheckCircle } from "phosphor-react";
import { formatCurrency } from "../../utils/formatters";

export default function InvoicePaymentModal({
  isOpen,
  onOpenChange,
  invoice,
  paymentAmount,
  setPaymentAmount,
  paymentMethod,
  setPaymentMethod,
  onSubmit,
}) {
  if (!invoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            Record Payment
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="bg-zinc-50 p-4 rounded-sm text-center">
              <p className="text-sm text-zinc-500">Balance Due</p>
              <p className="font-heading text-3xl font-bold text-amber-600">
                {formatCurrency(invoice.balance_due)}
              </p>
            </div>

            <div>
              <Label>Payment Amount *</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={invoice.balance_due}
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="h-12 border-2 pl-7 text-lg"
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-2 text-xs"
                  onClick={() => setPaymentAmount(invoice.balance_due?.toFixed(2))}
                >
                  Full Amount
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-2 text-xs"
                  onClick={() => setPaymentAmount((invoice.balance_due / 2).toFixed(2))}
                >
                  Half
                </Button>
              </div>
            </div>

            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1 h-12 border-2" data-testid="payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Credit/Debit Card</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {parseFloat(paymentAmount) === invoice.balance_due && (
              <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-sm">
                <CheckCircle size={18} />
                <span className="text-sm">This will mark the invoice as paid</span>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-2"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="confirm-payment-btn"
          >
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
